// Phase 13e — operator notification admin read schemas.
//
// Per 13e-1 — filter set: standard 8-field shape mirroring RefundListQuerySchema.
// Per 13e-2 — compact list / nested-deliveries detail (payload included full per 13e-4).
// Per 13e-3 — per-user variant `/admin/users/:id/notifications` reuses the list shape.
// Per 13e-6 — `/admin/suppressions` read-only paginated list.
//
// Convention 06 — one file per resource concern. All notification-admin shapes
// (notification list/detail + suppression list) live together because they are
// the admin read surface for the notification resource group.
import { z } from 'zod';
import { DeliveryStatusSchema, NotificationCategorySchema, NotificationChannelSchema, NotificationStatusSchema, } from './notification.schema.js';
// Mirrors prisma enum `SuppressionReason`.
export const SuppressionReasonSchema = z.enum([
    'HARD_BOUNCE',
    'SPAM_COMPLAINT',
    'MANUAL',
    'UNSUBSCRIBE_LINK',
    'SMS_STOP', // doc 14 (SMS-d) — recipient texted STOP
]);
// ─────────────────────────────────────────────────────────────────────────────
// Notification list (13e-1 + 13e-2)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Per 13e-1: 8 filter fields (status / category / recipientUserId /
 * templateCodeName / fromDate / toDate + page / limit). `templateCodeName`
 * is freeform here; the 9 valid values from TEMPLATE_CODE_NAMES are not
 * enforced at the schema level (a new template lands in v1.x without a
 * schema bump). Page/limit defaults mirror RefundListQuerySchema.
 */
export const NotificationListQuerySchema = z.object({
    status: NotificationStatusSchema.optional(),
    category: NotificationCategorySchema.optional(),
    recipientUserId: z.string().uuid().optional(),
    templateCodeName: z.string().optional(),
    fromDate: z.string().date().optional(),
    toDate: z.string().date().optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * Per 13e-2 compact list row. `codeName` = templateCodeName; `dispatchedAt`
 * is `min(deliveries.dispatchedAt)` collapsed to a single nullable
 * datetime so the list row stays flat.
 */
export const NotificationListItemSchema = z.object({
    id: z.string().uuid(),
    codeName: z.string(),
    status: NotificationStatusSchema,
    category: NotificationCategorySchema,
    recipientEmail: z.string(),
    recipientUserId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    dispatchedAt: z.string().datetime().nullable(),
});
export const NotificationListResponseSchema = z.object({
    items: z.array(NotificationListItemSchema),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    totalCount: z.number().int().min(0),
});
// ─────────────────────────────────────────────────────────────────────────────
// Notification detail (13e-2 + 13e-4)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Per-delivery shape nested into the detail response. Fields mirror
 * `NotificationDelivery` model + the Postmark provider metadata.
 */
export const NotificationDeliveryShapeSchema = z.object({
    id: z.string().uuid(),
    channel: NotificationChannelSchema,
    status: DeliveryStatusSchema,
    providerMessageId: z.string().nullable(),
    attemptCount: z.number().int().nonnegative(),
    lastErrorMessage: z.string().nullable(),
    dispatchedAt: z.string().datetime().nullable(),
    deliveredAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * Per 13e-2: full notification + nested deliveries.
 * Per 13e-4: `payload` is exposed plaintext to OperatorGuard-passing operators
 *            (magic-link tokens, reset URLs, AR amounts — all visible).
 */
export const NotificationDetailResponseSchema = z.object({
    id: z.string().uuid(),
    codeName: z.string(),
    status: NotificationStatusSchema,
    category: NotificationCategorySchema,
    recipientEmail: z.string(),
    recipientUserId: z.string().uuid().nullable(),
    sourceEventId: z.string().uuid(),
    payload: z.record(z.string(), z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deliveries: z.array(NotificationDeliveryShapeSchema),
});
// ─────────────────────────────────────────────────────────────────────────────
// Suppression list (13e-6)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Per 13e-6: `{email, category, page, limit}` query shape. Un-suppress + write
 * surfaces deferred to v1.x.
 */
export const NotificationSuppressionListQuerySchema = z.object({
    // Lowercased to match how suppression rows are stored + the reactivate write
    // path (recipientEmail flows through lowercased) — else an exact case-sensitive
    // Postgres match silently returns nothing for a mixed-case query.
    email: z.string().email().toLowerCase().optional(),
    category: NotificationCategorySchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const NotificationSuppressionListItemSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    category: NotificationCategorySchema,
    reason: SuppressionReasonSchema,
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const NotificationSuppressionListResponseSchema = z.object({
    items: z.array(NotificationSuppressionListItemSchema),
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    totalCount: z.number().int().min(0),
});
// Operator un-suppress (reactivate) — clears the address from BOTH our local
// suppression table AND (best-effort) Postmark's list. Destructive operator
// action, so a documented reason (>=10 chars) is required, mirroring the
// banking-reject convention.
export const NotificationSuppressionReactivateRequestSchema = z.object({
    email: z.string().email().max(254).toLowerCase(),
    reason: z.string().trim().min(10).max(500),
});
export const NotificationSuppressionReactivateResponseSchema = z.object({
    email: z.string(),
    /** How many local notification_suppression rows were removed (0..N categories). */
    localRowsDeleted: z.number().int().min(0),
    /** Whether Postmark accepted the provider-side reactivation (false when no
     *  token / dev, or the Postmark call errored — the local clear still applies). */
    postmarkCleared: z.boolean(),
});
//# sourceMappingURL=admin.schema.js.map